import {DashboardWrapper} from "../../../layouts/dashboard-wrapper";
import {StyledPageHeader, StyledPageWrapper} from "../../../styles/dashboard/calls/calls";
import {ProductListPage} from "../../../components/products/product-list";
import {
  StyledNoUpcomingCallBox,
  StyledPageHeaderWrapper,
} from "../../../components/calls/upcoming-calls/upcoming-calls.style";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {Alert, Button, Space, Tabs} from "antd";
import {ProductIcon} from "../../../components/icons/icons";
import {TourWidget} from "../../../components/widgets/tour-widget";
import {StyledCreateButtonWrapper} from "../../../layouts/dashboard-wrapper/dashboard-wrapper.style";
import {PlusOutlined} from "@ant-design/icons";
import {CanvaTemplateWidget} from "../../../components/widgets/canva-template-widget";
import {
  ProductsProvider,
  useProductsContext,
} from "../../../context/products/products-context";
import {ErrorBoundary} from "react-error-boundary";
import {ServicesEntryPoint} from "./services";
import {useEffect, useState} from "react";
import {GetStoreServicesOrdersContextProvider} from "../../../service-feature/useCases/get-orders/context";
import {useGetStoreServices} from "../../../react-query-hooks/store/services/get-services";
import {SubscriptionsEntryPoint} from "./subscriptions";
import {useSubscriptionsTableQuery} from "../../../react-query-hooks/store/subscriptions/subscriptions-table-query";

export type StoreTabKey = "digital-products" | "services" | "subscriptions";

const STORE_TAB_CTA_CONFIG = {
  "digital-products": {
    label: "Create a new product",
    link: "/products/new",
  },
  services: {
    label: "Create a new service",
    link: "/products/services/new",
  },
  subscriptions: {
    label: "Create a new subscription",
    link: "/products/subscriptions/new",
  },
} as const satisfies Record<StoreTabKey, {label: string; link: string}>;

/**
 * Resolves the store page create CTA label and route for the active tab.
 * Defaults to digital products when the tab key is unrecognized.
 */
function getStoreTabCta({activeTab}: {activeTab: StoreTabKey}) {
  return STORE_TAB_CTA_CONFIG[activeTab] ?? STORE_TAB_CTA_CONFIG["digital-products"];
}

export function StorePage() {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("activeTab");
  const [defaultActiveKey, setDefaultActiveKey] =
    useState<StoreTabKey>("digital-products");
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeTab) {
      return;
    }
    if (
      activeTab === "digital-products" ||
      activeTab === "services" ||
      activeTab === "subscriptions"
    ) {
      setDefaultActiveKey(activeTab);
    }
  }, [activeTab]);

  function handleTabClick(key: string) {
    if (key === "digital-products") {
      setDefaultActiveKey("digital-products");
      navigate("/products?activeTab=digital-products");
    }
    if (key === "services") {
      setDefaultActiveKey("services");
      navigate("/products?activeTab=services");
    }

    if (key === "subscriptions") {
      setDefaultActiveKey("subscriptions");
      navigate("/products?activeTab=subscriptions");
    }
  }
  return (
    <DashboardWrapper pageTitle="Products">
      <StyledPageWrapper>
        <div className="main-area">
          <ProductsProvider>
            <GetStoreServicesOrdersContextProvider>
              <StorePageEntryPoint
                defaultActiveKey={defaultActiveKey}
                handleTabClick={handleTabClick}
              />
            </GetStoreServicesOrdersContextProvider>
          </ProductsProvider>
        </div>
        <div className="widget-area">
          <Space direction="vertical" size={32} style={{display: "flex"}}>
            <TourWidget
              textInfo="Watch a video guide on how to create and sell products for your audience and clients."
              buttons={[
                <Button
                  href="https://www.loom.com/share/31b30bd926f64ac48bcacda15726f83b"
                  block
                  icon={<ProductIcon />}
                  size="small"
                  target="_blank"
                >
                  <span>Watch demo video</span>
                </Button>,
              ]}
            />
            <CanvaTemplateWidget url="https://www.canva.com/design/DAGrLqawTBU/90JAbcxK-8dANvatprmP8Q/view?utm_content=DAGrLqawTBU&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview" />
          </Space>
        </div>
      </StyledPageWrapper>
    </DashboardWrapper>
  );
}

export function StorePageEntryPoint({
  defaultActiveKey,
  handleTabClick,
}: {
  defaultActiveKey: StoreTabKey;
  handleTabClick(key: string): void;
}) {
  const storeTabCta = getStoreTabCta({activeTab: defaultActiveKey});
  const {state} = useProductsContext();
  const useGetStoreServicesQuery = useGetStoreServices(1, 5);
  const productLength = state.products ? state.products.length : 0;
  const servicesLength = useGetStoreServicesQuery.data
    ? useGetStoreServicesQuery.data.data.productServices.length
    : 0;

  const storeCount = productLength + servicesLength;

  /** When there are no products or services, peek at subscription list so subscription-only stores still get tabs. */
  const subscriptionsPeekQuery = useSubscriptionsTableQuery({
    enabled: storeCount === 0,
    page: 1,
    perPage: 1,
    searchQuery: "",
  });
  const subscriptionsPeekTotal = subscriptionsPeekQuery.data?.total ?? 0;
  const subscriptionsPeekMaybeLoading =
    storeCount === 0 &&
    subscriptionsPeekQuery.isFetching &&
    subscriptionsPeekQuery.data === undefined;

  const hasStoreTabs =
    storeCount > 0 || subscriptionsPeekTotal > 0 || subscriptionsPeekMaybeLoading;

  return (
    <>
      <StyledPageHeaderWrapper>
        <StyledPageHeader>Store</StyledPageHeader>
        {hasStoreTabs && (
          <StyledCreateButtonWrapper>
            <Link to={storeTabCta.link}>
              <Button block type="primary" size="small">
                <span className="show-desktop">{storeTabCta.label}</span>
                <span className="show-mobile">
                  <PlusOutlined style={{fontSize: "2.4rem"}} />
                </span>
              </Button>
            </Link>
          </StyledCreateButtonWrapper>
        )}
      </StyledPageHeaderWrapper>
      <div style={{marginTop: "4.6rem"}}>
        {hasStoreTabs ? (
          <Tabs
            defaultActiveKey={defaultActiveKey}
            activeKey={defaultActiveKey}
            onTabClick={(key) => handleTabClick(key)}
          >
            <Tabs.TabPane tab="Products" key="digital-products">
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <ProductListPage />
              </ErrorBoundary>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Services" key="services">
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <ServicesEntryPoint />
              </ErrorBoundary>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Subscriptions" key="subscriptions">
              <ErrorBoundary
                FallbackComponent={({error, resetErrorBoundary}) => (
                  <div style={{marginTop: "1.8rem"}}>
                    <Alert
                      message="Sorry, something went wrong."
                      showIcon
                      description={error.message}
                      type="error"
                      action={
                        <Button
                          onClick={() => {
                            resetErrorBoundary();
                          }}
                          size="small"
                          danger
                        >
                          Reset
                        </Button>
                      }
                    />
                  </div>
                )}
              >
                <SubscriptionsEntryPoint
                  listQueryEnabled={defaultActiveKey === "subscriptions"}
                />
              </ErrorBoundary>
            </Tabs.TabPane>
          </Tabs>
        ) : (
          <StyledNoUpcomingCallBox>
            <p className="info-text">
              You do not have any products, services, or subscriptions at the moment.
            </p>
            <Link to={storeTabCta.link}>
              <Button type="primary" size="small">
                {storeTabCta.label}
              </Button>
            </Link>
          </StyledNoUpcomingCallBox>
        )}
      </div>
    </>
  );
}
